import React, { Component } from 'react';
import { BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import { WithTranslation, withTranslation } from 'react-i18next';
import _ from 'lodash';
import { HttpRequest } from 'types/rest/analyticsTypes';
import { getTableOptions } from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import { HttpDataState } from 'store/analytics/data/analyticsDataTypes';
import InspectionHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionHeader';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import { REFRESH_HTTP_CALL_VIEW, SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import MixpanelService from 'utils/mixpanelService';
import {
  fetchHttpCallDetailThunk,
  fetchHttpCallsThunk,
} from '../../../../store/analytics/data/analyticsHttpCallsActions';
import {
  getCommonDataFromDataContext,
  getCommonDataFromUiContext,
  getFilteredHttpRequests,
  getHttpCallDetail,
  getHttpViewTableOptions,
} from '../Common/HttpViewUtils';
import {
  changeHttpCallsViewActivePage,
  changeHttpCallsViewPageSize,
  selectHttpCallsDetailTab,
  setHttpCallsViewSearchKeyword,
} from '../../../../store/analytics/ui/subViews/httpCallsView/httpCallsViewActions';
import { DetailTabType } from '../../../../store/analytics/ui/analyticsUiTypes';

interface HttpCallProps {
  /** Request to be saved in the modal */
  requestToBeSaved: HttpRequest | null;
}

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

interface WebhookViewProps {
  mockService: MockServiceAnalytics;
}

/**
 * List of http calls
 */
export class HttpCallView extends Component<AllProps, HttpCallProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    requestToBeSaved: null,
  };

  componentDidMount(): void {
    this.getHttpCallDataForFirstTime();
  }

  componentDidUpdate() {
    this.getHttpCallDataForFirstTime();
  }

  /**
   * Loads data on mount event / tab change when they are not loaded yet.
   */
  getHttpCallDataForFirstTime = () => {
    const contextsAreReady = !_.isNil(this.props.activeDataContext) && !_.isNil(this.props.activeUiContext);
    if (!contextsAreReady) {
      return;
    }

    if (
      this.props.activeUiContext!.activeTab === 'httpCall' &&
      this.props.activeDataContext.httpCall.isLoadingData === false &&
      this.props.activeDataContext.httpCall.lastRefreshTs === null &&
      this.props.activeDataContext.httpCall.isDetailRequested === false
    ) {
      this.props.getHttpCalls(this.props.activeTab);
    }
  };

  callRefresh = () => {
    MixpanelService.track(REFRESH_HTTP_CALL_VIEW(this.props.activeTab));
    this.props.getHttpCalls(this.props.activeTab);
  };

  handleFilterChange = async (value: string) => {
    await this.props.setHttpCallsViewSearchKeyword(value);
    this.sendMixPanelEvent();
  };

  sendMixPanelEvent = _.debounce(() => {
    MixpanelService.track(SEARCH_FILTER_RESULTS());
  }, 600);

  /**
   * Gets list of request from active data context in redux.
   */
  getHttpCallsFromDataContext = () => {
    if (_.isNil(this.props.activeDataContext)) {
      return [];
    }
    const txiDataState: HttpDataState = this.props.activeDataContext.httpCall;
    return (txiDataState.httpRequests as HttpRequest[]) || [];
  };

  callUpdate = (tabName: DetailTabType) => {
    this.props.selectDetails(tabName);
  };

  getDetail = () =>
    getHttpCallDetail(
      this.callUpdate,
      this.props.activeUiContext,
      'httpCall',
      this.props.activeDataContext,
      this.props.getHttpCalls,
    );

  render() {
    // UI state related data
    const { activePage, pageSize, selectedHttpReq, searchKeyword } = getCommonDataFromUiContext(
      this.props.activeUiContext,
      'httpCallView',
    );

    // Data state
    const { isRequestListLoading, lastRefresh, tabId } = getCommonDataFromDataContext(
      this.props.activeDataContext,
      'httpCall',
    );

    const httpCalls = this.getHttpCallsFromDataContext();
    const initResult = getFilteredHttpRequests(searchKeyword, this.getHttpCallsFromDataContext());
    const tableOptions: BlazeTableOptions = getHttpViewTableOptions(
      reqId => this.props.getDetail(reqId, tabId),
      selectedHttpReq,
      {
        pageSize: pageSize,
        total: httpCalls.length,
        activePage: activePage,
      },
      'httpCall',
    );
    const filterResult: FilteredResults = getTableOptions(initResult, searchKeyword, tableOptions, pageSize);

    return (
      <>
        <InspectionHeader
          refreshHandler={this.callRefresh}
          requests={httpCalls}
          onSearchKeywordChanged={this.handleFilterChange}
          lastRefresh={lastRefresh}
          searchKeyword={searchKeyword}
          title={'TRIGGERED HTTP CALLS'}
          msType={this.props.mockService.type}
        ></InspectionHeader>
        <BlazeTable
          isLoading={isRequestListLoading}
          options={filterResult.tableOptions}
          onPageSizeChange={this.props.changeHttpCallsViewPageSize}
          onPageChange={this.props.changeHttpCallsViewActivePage}
          data={filterResult.sliceData}
          onRef={(ref: any) => (this.tableRef = ref)}
          showPageSizeDropdown={true}
        />

        {/* Detail for selected http call, show it only if there is detail selected */}
        {!_.isNil(selectedHttpReq) && !_.isNil(lastRefresh) && this.getDetail()}
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    activeTab: analytics.ui.activeTab,
    activeUiContext: analytics.ui.activeUiContext,
    activeDataContext: analytics.data.activeDataContext,
  };
};

const mapDispatchToProps = {
  changeHttpCallsViewPageSize,
  changeHttpCallsViewActivePage,
  setHttpCallsViewSearchKeyword,
  getDetail: fetchHttpCallDetailThunk,
  getHttpCalls: fetchHttpCallsThunk,
  selectDetails: selectHttpCallsDetailTab,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & WithTranslation & DispatchProps & WebhookViewProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(HttpCallView));
